import * as React from 'react';
import { Button } from '@cotera/client/v0/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@cotera/client/v0/components/ui/dialog';
import { Input } from '@cotera/client/v0/components/ui/input';
import { Label } from '@cotera/client/v0/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@cotera/client/v0/components/ui/select';
import type { CocoInputPrimitiveType } from './coco-mention-types';

const INPUT_KEY_PATTERN = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

export type CocoInputMentionDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (inputKey: string, inputType: CocoInputPrimitiveType) => void;
};

export const CocoInputMentionDialog: React.FC<CocoInputMentionDialogProps> = ({
  open,
  onOpenChange,
  onConfirm,
}) => {
  const [name, setName] = React.useState('');
  const [inputType, setInputType] =
    React.useState<CocoInputPrimitiveType>('string');
  const [error, setError] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    if (open) {
      setName('');
      setInputType('string');
      setError(undefined);
    }
  }, [open]);

  const handleSubmit = React.useCallback(() => {
    const trimmed = name.trim();
    if (trimmed === '') {
      setError('Enter a name for this input.');
      return;
    }
    if (!INPUT_KEY_PATTERN.test(trimmed)) {
      setError(
        'Use a valid identifier: letters, numbers, underscore; start with a letter or underscore.'
      );
      return;
    }
    setError(undefined);
    onConfirm(trimmed, inputType);
    onOpenChange(false);
  }, [name, inputType, onConfirm, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" showCloseButton={true}>
        <DialogHeader>
          <DialogTitle>Add input</DialogTitle>
          <DialogDescription>
            Define a named input and its type. It will appear as a mention in
            your prompt.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="grid gap-2">
            <Label htmlFor="coco-input-name">Name</Label>
            <Input
              id="coco-input-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (error !== undefined) {
                  setError(undefined);
                }
              }}
              placeholder="e.g. customerId"
              autoComplete="off"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
            />
            {error !== undefined ? (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            ) : null}
          </div>
          <div className="grid gap-2">
            <Label htmlFor="coco-input-type">Type</Label>
            <Select
              value={inputType}
              onValueChange={(v) => setInputType(v as CocoInputPrimitiveType)}
            >
              <SelectTrigger id="coco-input-type" className="w-full">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="string">String</SelectItem>
                <SelectItem value="number">Number</SelectItem>
                <SelectItem value="boolean">Boolean</SelectItem>
                <SelectItem value="super">
                  {'Super (Record<string, unknown>)'}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit}>
            Insert
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
